import { createRoute, type AnyRoute } from '@tanstack/react-router';

import config from '@/config';
import { TemplatePoets } from '@shared/config';

import { issueSearchSchema } from '@pages/issue/model/issue.hooks';

import { createTenantApp } from '@/app/create-tenant-app';
import { RouteCatalogKey } from '@/app/routing/route-catalog';
import { homeIssuesLoader } from '@/app/routing/home-loaders';
import { tokenAuthGuard } from '@/app/routing/token-auth-guard';
import { loginAuthGuard } from '@/app/routing/login-auth-guard';

import { homeSearchSchema } from './home/home.hooks';

// Pages
import Issue from '@pages/issue';
import Home from './home/home';
import Feed from './feed/feed';

const routes: RouteCatalogKey[] = [
	'issue',
	'issueArticle',
	'drafts',
	// The offline reader behind the AppMenu's Downloads entry
	// (`widgets/downloads-drawer`).
	'downloadedIssue',
	'downloadedIssueArticle',
];

const template = config.app.template as TemplatePoets;
const { skipTOC = false, feed = false } =
	config.app.template?.type === 'poets' ? (template.config ?? {}) : {};

if (config.app.template?.type === 'poets' && !skipTOC) {
	routes.push('previewView');
}

export const router = createTenantApp({
	home: {
		path: '/',
		validateSearch: homeSearchSchema,
		component: Home,
		loader: homeIssuesLoader,
		// `redirectIfUnauthenticated: false` -- home has to stay reachable
		// with no session at all, since it's what shows the token/login
		// entry point in the first place. Still runs the token-exchange/
		// URL-cleanup half of the guard when a reader arrives via `?token=`.
		beforeLoad: (opts) =>
			tokenAuthGuard(
				{
					params: opts.params,
					search: opts.search as Record<string, unknown>,
					pathname: opts.location.pathname,
				},
				{ redirectIfUnauthenticated: false },
			),
	},
	routes,
	// `issue`/`issueArticle` (the catalog's `issues/$id`/`issues/$id/$articleId`)
	// -- 'auth' resolves against the `auth` wiring below, per the tenant's
	// actual `config.app.auth.type`. Without this, both fall back to the
	// catalog's `DEFAULT_ISSUE_PROTECTION` (`tokenAuthGuard`, a no-op unless
	// the tenant is on `token` auth) -- poets' real tenants are on `login`
	// auth, so that default silently protected nothing.
	protect: { issue: 'auth', issueArticle: 'auth' },
	auth: {
		guards: {
			login: loginAuthGuard,
			// Preserves the catalog default's `token`-auth coverage now that
			// `protect.issue`/`protect.issueArticle` above replace it outright.
			token: (context) =>
				tokenAuthGuard(context, { redirectIfUnauthenticated: true }),
		},
	},
	// `issues` (no $id, same guards as issues/$id) and `feed`
	// (config.app.template.config.feed-gated) are both poets-exclusive.
	custom: (rootRoute) => {
		const customRoutes: AnyRoute[] = [
			createRoute({
				getParentRoute: () => rootRoute,
				path: 'issues',
				validateSearch: issueSearchSchema,
				component: Issue,
				beforeLoad: async (opts) => {
					await tokenAuthGuard(
						{
							params: opts.params,
							search: opts.search as Record<string, unknown>,
							pathname: opts.location.pathname,
						},
						{ redirectIfUnauthenticated: true },
					);
					loginAuthGuard();
				},
			}),
		];
		if (config.app.template?.type === 'poets' && feed) {
			customRoutes.push(
				createRoute({
					getParentRoute: () => rootRoute,
					component: Feed,
					path: 'feed',
				}),
			);
		}
		return customRoutes;
	},
	// Same entries as basic, plus Sign out (poets' `login`/`jwt` tenants have
	// no Cognito profile menu to hold it). A tenant still needs
	// `config.app.favorite`/`config.app.downloadedIssues.enabled` to see
	// those two (@shared/ui/app-menu's own gating).
	appMenu: ['favorites', 'downloadedIssues', 'install', 'about', 'signOut'],
});
